import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@amzn/meridian/app-layout';
import Masthead, { MastheadTitle, MastheadMenuButton } from '@amzn/meridian/masthead';
import SideMenu, { SideMenuLink, SideMenuTitle } from '@amzn/meridian/side-menu';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import asleepTokens from '@amzn/meridian-tokens/base/icon/asleep';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { setAuthToken } from '../services/api';

interface PageLayoutProps {
    children: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;
    const { mode, setMode } = useTheme();
    const { user, token } = useAuth();
    const { hasFullAccess } = usePermissions();
    const [sideMenuOpen, setSideMenuOpen] = useState(true);
    const [openLinkHash, setOpenLinkHash] = useState<string | null>(null);

    useEffect(() => {
        if (token) setAuthToken(token);
    }, [token]);

    useEffect(() => {
        if (['/', '/amzl-updates'].includes(path)) setOpenLinkHash('#ds');
        else if (['/d1-amzl-capacity', '/d2-amzl-capacity', '/adhoc-deployments'].includes(path)) setOpenLinkHash('#capacity');
        else if (['/control-room', '/configuration'].includes(path)) setOpenLinkHash('#central');
        else if (path === '/ops-monitor') setOpenLinkHash('#monitoring');
    }, [path]);

    return (
        <AppLayout
            headerComponent={Masthead}
            sidebarComponent={SideMenu}
            spacingInset="450"
            backgroundColor="principal"
        >
            <Masthead size="medium">
                <MastheadMenuButton onClick={() => setSideMenuOpen(!sideMenuOpen)} aria-expanded={sideMenuOpen} />
                <MastheadTitle href="/" onClick={(href: string) => navigate(href)}>
                    Fortaleza
                </MastheadTitle>
            </Masthead>
            <div style={{ position: 'fixed', top: '0', right: '16px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '12px', height: '48px' }}>
                {user && (
                    <Row spacing="300" alignmentVertical="center">
                        <Text>Welcome, {user}</Text>
                        <img
                            src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${user}`}
                            alt={user}
                            style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    </Row>
                )}
                <Row spacing="200" alignmentVertical="center">
                    <Toggle
                        checked={mode === 'dark'}
                        onChange={(checked: boolean) => setMode(checked ? 'dark' : 'light')}
                        aria-label="Toggle dark mode"
                    />
                    <Icon tokens={asleepTokens} color="primary" />
                </Row>
            </div>

            <SideMenu
                open={sideMenuOpen}
                onClose={() => setSideMenuOpen(false)}
            >
                <SideMenuTitle>
                    <Heading level={5}>Capacity Central</Heading>
                </SideMenuTitle>

                {/* Public — visible to ALL authenticated users */}
                <SideMenuLink
                    selected={['/', '/amzl-updates'].includes(path)}
                    open={openLinkHash === '#ds'}
                    onClick={() => {
                        setOpenLinkHash(openLinkHash === '#ds' ? null : '#ds');
                    }}
                >
                    Delivery Station
                    <SideMenuLink
                        href="/amzl-updates"
                        selected={path === '/' || path === '/amzl-updates'}
                        onClick={(href: string) => {
                            navigate(href);
                        }}
                        onEscape={() => setOpenLinkHash(null)}
                    >
                        AMZL Updates
                    </SideMenuLink>
                </SideMenuLink>

                {/* Protected — only visible to users with Bindle Lock */}
                {hasFullAccess && (
                    <SideMenuLink
                        selected={['/d1-amzl-capacity', '/d2-amzl-capacity', '/adhoc-deployments'].includes(path)}
                        open={openLinkHash === '#capacity'}
                        onClick={() => {
                            setOpenLinkHash(openLinkHash === '#capacity' ? null : '#capacity');
                        }}
                    >
                        Capacity Management
                        <SideMenuLink
                            href="/d1-amzl-capacity"
                            selected={path === '/d1-amzl-capacity'}
                            onClick={(href: string) => {
                                navigate(href);
                            }}
                            onEscape={() => setOpenLinkHash(null)}
                        >
                            D-1 AMZL
                        </SideMenuLink>
                        <SideMenuLink
                            href="/d2-amzl-capacity"
                            selected={path === '/d2-amzl-capacity'}
                            onClick={(href: string) => {
                                navigate(href);
                            }}
                            onEscape={() => setOpenLinkHash(null)}
                        >
                            D-2 AMZL
                        </SideMenuLink>
                        <SideMenuLink
                            href="/adhoc-deployments"
                            selected={path === '/adhoc-deployments'}
                            onClick={(href: string) => {
                                navigate(href);
                            }}
                            onEscape={() => setOpenLinkHash(null)}
                        >
                            Adhoc Deployments
                        </SideMenuLink>
                    </SideMenuLink>
                )}

                {hasFullAccess && (
                    <SideMenuLink
                        selected={['/control-room', '/configuration'].includes(path)}
                        open={openLinkHash === '#central'}
                        onClick={() => {
                            setOpenLinkHash(openLinkHash === '#central' ? null : '#central');
                        }}
                    >
                        Central Operations
                        <SideMenuLink
                            href="/control-room"
                            selected={path === '/control-room'}
                            onClick={(href: string) => {
                                navigate(href);
                            }}
                            onEscape={() => setOpenLinkHash(null)}
                        >
                            Control Room
                        </SideMenuLink>
                        <SideMenuLink
                            href="/configuration"
                            selected={path === '/configuration'}
                            onClick={(href: string) => {
                                navigate(href);
                            }}
                            onEscape={() => setOpenLinkHash(null)}
                        >
                            System Configuration
                        </SideMenuLink>
                    </SideMenuLink>
                )}

                {hasFullAccess && (
                    <SideMenuLink
                        selected={path === '/ops-monitor'}
                        open={openLinkHash === '#monitoring'}
                        onClick={() => {
                            setOpenLinkHash(openLinkHash === '#monitoring' ? null : '#monitoring');
                        }}
                    >
                        Monitoring
                        <SideMenuLink
                            href="/ops-monitor"
                            selected={path === '/ops-monitor'}
                            onClick={(href: string) => {
                                navigate(href);
                            }}
                            onEscape={() => setOpenLinkHash(null)}
                        >
                            Ops Monitor
                        </SideMenuLink>
                    </SideMenuLink>
                )}

            </SideMenu>

            {children}
        </AppLayout>
    );
};

export default PageLayout;
